import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inr, periodLabel, type Payslip } from '@hrms/shared';
import { api } from '../../../lib/api';
import { openFile } from '../../../lib/files';
import { BarChart, Button, Card, DataTable, PageHeader, Screen, Text } from '../../../ui';

export function PayslipsScreen() {
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ['payslips', 'mine', page], queryFn: () => api.payroll.payslips({ page }) });
  const recent = [...(q.data?.data ?? [])].sort((a, b) => a.period.localeCompare(b.period)).slice(-12);
  const open = (p: Payslip) => openFile(api.payroll.payslipHtmlPath(p.id));
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()}>
      <PageHeader title="Payslips" subtitle="Tap a month to open the payslip." />
      {recent.length > 1 && (
        <Card title="Net pay trend">
          <BarChart data={recent.map((p) => ({ label: p.period.slice(2), value: p.net_pay }))} format={(n) => `${Math.round(n / 1000)}k`} />
        </Card>
      )}
      <DataTable
        rows={q.data}
        loading={q.isLoading}
        onPageChange={setPage}
        onRowPress={open}
        emptyTitle="No payslips yet"
        columns={[
          { key: 'period', title: 'Month', width: 140, render: (p) => periodLabel(p.period), sortValue: (p) => p.period },
          { key: 'payable_days', title: 'Paid days', width: 90, align: 'right', render: (p) => `${p.payable_days}/${p.days_in_month}`, sortValue: (p) => p.payable_days },
          { key: 'gross', title: 'Gross', width: 110, align: 'right', render: (p) => inr(p.gross), sortValue: (p) => p.gross },
          { key: 'total_deductions', title: 'Deductions', width: 110, align: 'right', render: (p) => inr(p.total_deductions), sortValue: (p) => p.total_deductions },
          { key: 'tds', title: 'TDS', width: 100, align: 'right', render: (p) => inr(p.tds), sortValue: (p) => p.tds },
          { key: 'net_pay', title: 'Net pay', width: 120, align: 'right', render: (p) => <Text weight="semibold">{inr(p.net_pay)}</Text>, sortValue: (p) => p.net_pay },
          { key: 'open', title: '', width: 90, sortable: false, render: (p) => <Button size="sm" icon="eye" title="View" onPress={() => open(p)} /> },
        ]}
      />
    </Screen>
  );
}
